import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@xing-com/button';
import type { PurchaseOrderType } from '@xing-com/crate-common-graphql-types';
import { LoginState, ProductType } from '@xing-com/crate-common-graphql-types';
import { navigate, useLocation } from '@xing-com/crate-core-router';
import { WebOrderDocument } from '@xing-com/crate-upsell-graphql';
import { useLoginAppUrl } from '@xing-com/crate-xinglet';
import { useLoginState } from '@xing-com/hub';

export interface OfferBoxButtonProps {
  orderType: PurchaseOrderType;
  activeContractId: string;
  duration: number;
  product: ProductType;
}

export const OfferBoxButton: React.FC<OfferBoxButtonProps> = (props) => {
  const { search } = useLocation();
  const { activeContractId, duration, product, orderType } = props;
  const loginState = useLoginState();
  const externalLoginUrl = useLoginAppUrl();
  const [canRedirect, setCanRedirect] = useState(false);

  useEffect(() => {
    if (canRedirect) {
      navigate(
        `/purchase/confirmation/${product}?sc_o=${product}_cancellation_overview_revoke_cancellation_click`,
        {
          state: {
            __typeHint: 'CANCELLATION_REVOCATION_SUCCESS',
            product,
          },
        }
      );
    }
  }, [canRedirect, product]);

  const [placeOrder, { loading: loadingAcceptRevocation }] = useMutation(
    WebOrderDocument,
    {
      onError: (error) => {
        throw error;
      },
      onCompleted: ({ results }) => {
        if (results?.success) {
          setCanRedirect(true);
        }
      },
    }
  );

  if (orderType === 'REVOKE' && activeContractId) {
    const handleRevokeAcceptance = async (): Promise<void> => {
      await placeOrder({
        variables: { input: { contractId: activeContractId } },
      });
    };
    return (
      <Button
        data-testid="revoke-offer-confirmation-button"
        size="medium"
        variant={
          product === ProductType.Projobs ? 'proJobs' : ProductType.Premium
        }
        onClick={handleRevokeAcceptance}
        loading={loadingAcceptRevocation}
      >
        <FormattedMessage
          id={`UPSELL_CANCELLATION_REVOKE_${product.toUpperCase()}_ACCEPTANCE_BUTTON`}
        />
      </Button>
    );
  }

  return (
    <Button
      data-testid="checkout-button"
      variant={
        product === ProductType.Projobs ? 'proJobs' : ProductType.Premium
      }
      // @ts-expect-error FIXME: SC6
      href={
        loginState === LoginState.LoggedOut &&
        `${externalLoginUrl}/continue?dest_url=/purchase/checkout/${product}/${activeContractId}${search ? `&${search}` : ''}&login_channel=upsellfunnel`
      }
      // @ts-expect-error FIXME: SC6
      to={
        loginState === LoginState.LoggedIn
          ? {
              pathname: `/purchase/checkout/${product}/${activeContractId}`,
              search,
            }
          : undefined
      }
    >
      <FormattedMessage
        id="UPSELL_PURCHASE_UPSELL_BUTTON"
        values={{ duration }}
      />
    </Button>
  );
};
